import { Box, InputBase, Typography } from '@mui/material'
import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { isValidEmail } from '../../../../utils/email'
import { SelectionChip } from '../../../customComponents/SelectionChip'
import { styles } from './InviteeList.styles'

type EntryElement = HTMLInputElement | HTMLTextAreaElement

const INPUT_ID = 'p2pInviteEmails'

interface Props {
  emails: string[]
  onChange: (emails: string[]) => void
  onInvalidChange?: (hasInvalid: boolean) => void
}

interface InvalidEntry {
  value: string
  reason: string
}

export const EmailChipField = ({ emails, onChange, onInvalidChange }: Props): JSX.Element => {
  const [input, setInput] = useState('')
  const [invalid, setInvalid] = useState<InvalidEntry[]>([])
  const inputRef = useRef<EntryElement>(null)

  useEffect(() => { onInvalidChange?.(invalid.length > 0) }, [invalid, onInvalidChange])

  const tokenize = (text: string): string[] =>
    text.split(/[,;\n\r\t]+/).map(t => t.trim()).filter(t => t.length > 0)

  const commit = (tokens: string[]): void => {
    const nextValid = [...emails]
    const nextInvalid: InvalidEntry[] = [...invalid]
    const seen = new Set(emails.map(e => e.toLowerCase()))

    tokens.forEach(token => {
      if (!isValidEmail(token)) {
        if (!nextInvalid.some(i => i.value === token)) nextInvalid.push({ value: token, reason: 'Not a valid email address' })
        return
      }
      const lower = token.toLowerCase()
      if (!seen.has(lower)) {
        seen.add(lower)
        nextValid.push(token)
      }
    })

    onChange(nextValid)
    setInvalid(nextInvalid)
  }

  const handleKeyDown = (event: KeyboardEvent<EntryElement>): void => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault()
      if (input.trim().length > 0) {
        commit(tokenize(input))
        setInput('')
      }
    } else if (event.key === 'Backspace' && input.length === 0 && emails.length > 0) {
      onChange(emails.slice(0, -1))
    }
  }

  const handlePaste = (event: ClipboardEvent<EntryElement>): void => {
    const pasted = event.clipboardData.getData('text')
    if (/[,;\n\r\t]/.test(pasted)) {
      event.preventDefault()
      commit(tokenize(pasted))
      setInput('')
    }
  }

  const removeEmail = (email: string): void => { onChange(emails.filter(e => e !== email)) }
  const removeInvalid = (value: string): void => { setInvalid(invalid.filter(i => i.value !== value)) }
  const isEmpty = emails.length === 0 && invalid.length === 0

  return (
    <Box data-testid='p2pEmailChipField'>
      <Typography component='label' htmlFor={INPUT_ID} variant='body1' sx={styles.fieldLabel}>Email addresses</Typography>
      <Box sx={styles.chipBox} onClick={() => { inputRef.current?.focus() }}>
        {emails.map(email => (
          <SelectionChip key={email} label={email} onDelete={() => { removeEmail(email) }} dataTestId={`p2pEmailChip-${email}`} />
        ))}
        {invalid.map(entry => (
          <SelectionChip
            key={entry.value}
            label={entry.value}
            invalid
            onDelete={() => { removeInvalid(entry.value) }}
            dataTestId={`p2pInvalidChip-${entry.value}`}
          />
        ))}
        <InputBase
          id={INPUT_ID}
          multiline
          inputRef={inputRef}
          placeholder={isEmpty ? 'Example@email.com' : ''}
          value={input}
          onChange={(e) => { setInput(e.target.value) }}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (input.trim().length > 0) { commit(tokenize(input)); setInput('') } }}
          inputProps={{ 'data-testid': 'p2pEmailChipInput', onPaste: handlePaste }}
          sx={styles.chipInput}
        />
      </Box>
      <Typography variant='body2' sx={styles.fieldHelper}>
        Invitations can&apos;t be sent to distribution lists — enter individual email addresses.
      </Typography>
      {invalid.length > 0 &&
        <Typography variant='body1' color='error' sx={styles.fieldError} data-testid='p2pInvalidSummary'>
          {invalid.length === 1 ? '1 address looks' : `${invalid.length} addresses look`} invalid. Fix or remove before sending.
        </Typography>}
    </Box>
  )
}
